import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { DateTime } from 'luxon';
import PaymentsOverview from './PaymentsOverview';
import PendingPayoutsSection from './PendingPayoutsSection';
import PaymentDueSection from './PaymentDueSection';
import ReviewHeldPaymentsSection from './ReviewHeldPaymentsSection';
import CardPaymentsSection from './CardPaymentsSection';
import NegativeBalanceWorklist from './NegativeBalanceWorklist';
import './wallet.css';

// The queues on this page (owner, 2026-09-07): each one reports its row count
// up and renders NOTHING when it is empty, so a quiet day is the overview
// cards plus the card-payments ledger — not five "No pending…" placeholders.
const QUEUE_KEYS = ['deposits', 'belowZero', 'held', 'payouts', 'issues', 'recon'];

export default function PendingDeposits() {
    const [deposits, setDeposits] = useState([]);
    const [loading, setLoading] = useState(true);
    const [attention, setAttention] = useState({});
    const [gateway, setGateway] = useState(null);
    // One STABLE callback per queue: the sections keep these in their fetch
    // hooks' dependency lists, so a fresh function per render would re-run
    // every fetch on every render.
    const counters = useMemo(() => Object.fromEntries(QUEUE_KEYS.map((key) => [
        key,
        (n) => setAttention((a) => (a[key] === n ? a : { ...a, [key]: n })),
    ])), []);
    const onReconSummary = useCallback((summary) => {
        setGateway(summary);
        counters.recon(summary ? Number(summary.issues || 0) : 0);
    }, [counters]);
    const [confirmAction, setConfirmAction] = useState(null); // { type: 'approve'|'reject'|'edit', deposit }
    const [rejectNotes, setRejectNotes] = useState('');
    const [processing, setProcessing] = useState(false);

    // Edit form state
    const [editAmount, setEditAmount] = useState('');
    const [editBonus, setEditBonus] = useState('');
    const [editNotes, setEditNotes] = useState('');

    const userRole = (localStorage.getItem('userRole') || '').toLowerCase();
    const isManager = userRole === 'manager';

    const money = (n) => `AED ${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    const formatDate = (dateString) => {
        if (!dateString) return '—';
        return DateTime.fromISO(dateString, { zone: 'Asia/Dubai' }).toFormat('dd MMM yy, HH:mm');
    };

    const fetchDeposits = useCallback(async () => {
        try {
            setLoading(true);
            const res = await axios.get('/api/wallet/deposits/pending');
            setDeposits(res.data);
        } catch (err) {
            console.error('Error fetching pending deposits:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchDeposits();
    }, [fetchDeposits]);

    useEffect(() => {
        if (!loading) counters.deposits(deposits.length);
    }, [loading, deposits.length, counters]);

    const openEdit = (deposit) => {
        setEditAmount(String(deposit.amount));
        setEditBonus(String(deposit.bonus_amount));
        setEditNotes(deposit.admin_notes || '');
        setConfirmAction({ type: 'edit', deposit });
    };

    const closeModal = () => {
        setConfirmAction(null);
        setRejectNotes('');
        setEditAmount('');
        setEditBonus('');
        setEditNotes('');
    };

    const handleApprove = async () => {
        if (!confirmAction) return;
        setProcessing(true);
        try {
            await axios.put(`/api/wallet/deposits/${confirmAction.deposit.id}/approve`);
            toast.success(`Deposit of ${money(confirmAction.deposit.amount)} approved`);
            closeModal();
            fetchDeposits();
        } catch (err) {
            console.error('Error approving deposit:', err);
            toast.error(err.response?.data?.error || 'Failed to approve deposit');
        } finally {
            setProcessing(false);
        }
    };

    const handleReject = async () => {
        if (!confirmAction) return;
        setProcessing(true);
        try {
            await axios.put(`/api/wallet/deposits/${confirmAction.deposit.id}/reject`, {
                admin_notes: rejectNotes
            });
            toast.success('Deposit rejected');
            closeModal();
            fetchDeposits();
        } catch (err) {
            console.error('Error rejecting deposit:', err);
            toast.error(err.response?.data?.error || 'Failed to reject deposit');
        } finally {
            setProcessing(false);
        }
    };

    const handleEditSave = async () => {
        if (!confirmAction) return;
        const amountNum = parseFloat(editAmount);
        const bonusNum = parseFloat(editBonus);
        if (isNaN(amountNum) || amountNum < 10) {
            toast.error('Amount must be at least 10 AED');
            return;
        }
        if (isNaN(bonusNum) || bonusNum < 0) {
            toast.error('Bonus must be 0 or more');
            return;
        }
        setProcessing(true);
        try {
            await axios.put(`/api/wallet/deposits/${confirmAction.deposit.id}`, {
                amount: amountNum,
                bonus_amount: bonusNum,
                admin_notes: editNotes.trim() || null,
            });
            toast.success('Deposit updated');
            closeModal();
            fetchDeposits();
        } catch (err) {
            console.error('Error updating deposit:', err);
            toast.error(err.response?.data?.error || 'Failed to update deposit');
        } finally {
            setProcessing(false);
        }
    };

    // Live preview of new total credit in edit modal
    const editTotalCredit = (() => {
        const a = parseFloat(editAmount);
        const b = parseFloat(editBonus);
        if (isNaN(a) || isNaN(b)) return 0;
        return a + b;
    })();

    const editBonusPct = (() => {
        const a = parseFloat(editAmount);
        const b = parseFloat(editBonus);
        if (isNaN(a) || isNaN(b) || a <= 0) return 0;
        return ((b / a) * 100).toFixed(2);
    })();

    return (
        <div className="swallet">
            <Link to="/wallet" className="swallet__back">
                ← Back to Wallet Management
            </Link>

            <div className="swallet__header">
                <h1 className="swallet__title">Payments</h1>
            </div>

            <PaymentsOverview attention={attention} gateway={gateway} />

            {/* Bank-transfer deposits waiting for a manager. Hidden entirely when
                there are none — the overview row says so. */}
            {!loading && deposits.length > 0 && (
            <div className="swallet__section" id="sec-deposits">
                <div className="swallet__sectionHead">
                    <h2 className="swallet__sectionTitle">
                        Pending Deposits
                        <span className="swallet__sectionBadge">{deposits.length}</span>
                    </h2>
                    <span className="swallet__sectionSub">
                        Bank transfers the client says they sent — check the proof, then approve or reject.
                    </span>
                </div>
                <table className="swallet__table">
                    <thead>
                        <tr>
                            <th>Date</th>
                            <th>Client</th>
                            <th>Amount</th>
                            <th>Bonus</th>
                            <th>Total Credit</th>
                            <th>Method</th>
                            <th>Bank Ref</th>
                            <th>Proof</th>
                            {isManager && <th>Actions</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {deposits.map((d) => (
                            <tr key={d.id}>
                                <td>{formatDate(d.created_at)}</td>
                                <td style={{ fontWeight: 900 }}>{d.client_name}</td>
                                <td>{money(d.amount)}</td>
                                <td>
                                    {parseFloat(d.bonus_amount) > 0
                                        ? `${money(d.bonus_amount)} (${d.bonus_percentage}%)`
                                        : '—'}
                                </td>
                                <td style={{ fontWeight: 900 }}>{money(d.total_credit)}</td>
                                <td>{d.payment_method === 'bank_transfer' ? 'Bank Transfer' : 'Online'}</td>
                                <td>{d.bank_reference || '—'}</td>
                                <td>
                                    {d.proof_of_payment_url ? (
                                        <a href={d.proof_of_payment_url} target="_blank" rel="noopener noreferrer" className="swallet__proofLink">
                                            View
                                        </a>
                                    ) : '—'}
                                </td>
                                {isManager && (
                                    <td>
                                        <div className="swallet__btnGroup">
                                            <button
                                                className="swallet__btn"
                                                onClick={() => openEdit(d)}
                                                title="Edit amount / bonus"
                                            >
                                                Edit
                                            </button>
                                            <button
                                                className="swallet__btn swallet__btn--success"
                                                onClick={() => setConfirmAction({ type: 'approve', deposit: d })}
                                            >
                                                Approve
                                            </button>
                                            <button
                                                className="swallet__btn swallet__btn--danger"
                                                onClick={() => setConfirmAction({ type: 'reject', deposit: d })}
                                            >
                                                Reject
                                            </button>
                                        </div>
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            )}

            {/* Every queue below renders nothing when empty. The wrapper ids are
                the scroll targets of the "Needs attention" chips above. */}
            {/* Clients already below zero — lived on the retired /wallet
                page; the queue page is where staff act, so it acts from here. */}
            <div id="sec-belowzero"><NegativeBalanceWorklist onCountChange={counters.belowZero} /></div>

            {/* Gateway fraud-review holds — time-sensitive (the automatic release
                fires at 60 minutes). */}
            <div id="sec-held"><ReviewHeldPaymentsSection onCountChange={counters.held} /></div>

            {/* Client payout requests (manual bank transfer) */}
            <div id="sec-payouts"><PendingPayoutsSection onCountChange={counters.payouts} /></div>

            {/* Price adjustments (and disputes) RETIRED (2026-07-21). */}

            {/* Saved-card auto-charge failures (payment-due) + reconciliation
                worklist; its 7-day gateway summary shows on the overview cards. */}
            <div id="sec-issues">
                <PaymentDueSection onCountChange={counters.issues} onReconSummary={onReconSummary} />
            </div>

            {/* Searchable ledger of every card payment taken (bookings, invoice
                settlements, top-ups), each with its reference + related record */}
            <CardPaymentsSection />

            {/* Confirmation / Edit Modal */}
            {confirmAction && (
                <div className="swallet__overlay" onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}>
                    <div className="swallet__modal">
                        {confirmAction.type === 'approve' && (
                            <>
                                <h3 className="swallet__modalTitle">Approve Deposit</h3>
                                <p className="swallet__modalText">
                                    Approve deposit of <strong>{money(confirmAction.deposit.amount)}</strong> from <strong>{confirmAction.deposit.client_name}</strong>?
                                    {parseFloat(confirmAction.deposit.bonus_amount) > 0 && (
                                        <><br />This will credit <strong>{money(confirmAction.deposit.total_credit)}</strong> to their wallet (including {money(confirmAction.deposit.bonus_amount)} bonus).</>
                                    )}
                                </p>
                                <div className="swallet__modalActions">
                                    <button className="swallet__btn" onClick={closeModal} disabled={processing}>
                                        Cancel
                                    </button>
                                    <button className="swallet__btn swallet__btn--success" onClick={handleApprove} disabled={processing}>
                                        {processing ? 'Processing...' : 'Confirm Approve'}
                                    </button>
                                </div>
                            </>
                        )}

                        {confirmAction.type === 'reject' && (
                            <>
                                <h3 className="swallet__modalTitle">Reject Deposit</h3>
                                <p className="swallet__modalText">
                                    Reject deposit of <strong>{money(confirmAction.deposit.amount)}</strong> from <strong>{confirmAction.deposit.client_name}</strong>?
                                </p>
                                <div className="swallet__formGroup" style={{ marginBottom: 16 }}>
                                    <label className="swallet__formLabel">Reason for rejection (optional)</label>
                                    <textarea
                                        className="swallet__formTextarea"
                                        style={{ height: 60 }}
                                        value={rejectNotes}
                                        onChange={(e) => setRejectNotes(e.target.value)}
                                        placeholder="Enter reason..."
                                    />
                                </div>
                                <div className="swallet__modalActions">
                                    <button className="swallet__btn" onClick={closeModal} disabled={processing}>
                                        Cancel
                                    </button>
                                    <button className="swallet__btn swallet__btn--danger" onClick={handleReject} disabled={processing}>
                                        {processing ? 'Processing...' : 'Confirm Reject'}
                                    </button>
                                </div>
                            </>
                        )}

                        {confirmAction.type === 'edit' && (
                            <>
                                <h3 className="swallet__modalTitle">Edit Pending Deposit</h3>
                                <p className="swallet__modalText">
                                    Adjust the deposit for <strong>{confirmAction.deposit.client_name}</strong>. Use this when the actual bank transfer amount doesn't match what the client entered.
                                </p>

                                <div className="swallet__formRow">
                                    <div className="swallet__formGroup">
                                        <label className="swallet__formLabel">Cash Amount (AED)</label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            min="10"
                                            className="swallet__formInput"
                                            value={editAmount}
                                            onChange={(e) => setEditAmount(e.target.value)}
                                        />
                                    </div>
                                    <div className="swallet__formGroup">
                                        <label className="swallet__formLabel">Bonus Amount (AED)</label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            className="swallet__formInput"
                                            value={editBonus}
                                            onChange={(e) => setEditBonus(e.target.value)}
                                        />
                                    </div>
                                </div>

                                <div style={{ margin: '12px 0', padding: '10px 14px', background: '#f9fafb', borderRadius: 10, border: '1px solid #e5e7eb' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#6b7280', fontWeight: 700 }}>
                                        <span>Total Credit</span>
                                        <span style={{ color: '#111827' }}>{money(editTotalCredit)}</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#9ca3af', marginTop: 4 }}>
                                        <span>Effective bonus</span>
                                        <span>{editBonusPct}%</span>
                                    </div>
                                </div>

                                <div className="swallet__formGroup" style={{ marginBottom: 16 }}>
                                    <label className="swallet__formLabel">Admin Notes (optional)</label>
                                    <textarea
                                        className="swallet__formTextarea"
                                        style={{ height: 60 }}
                                        value={editNotes}
                                        onChange={(e) => setEditNotes(e.target.value)}
                                        placeholder="e.g., Adjusted to match actual bank transfer amount"
                                    />
                                </div>

                                <div className="swallet__modalActions">
                                    <button className="swallet__btn" onClick={closeModal} disabled={processing}>
                                        Cancel
                                    </button>
                                    <button className="swallet__btn swallet__btn--primary" onClick={handleEditSave} disabled={processing}>
                                        {processing ? 'Saving...' : 'Save Changes'}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
